import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useAdmin } from '@/contexts/AdminContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import RichTextEditor from '@/components/ui/RichTextEditor';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectGroup,
  SelectLabel,
} from '@/components/ui/select';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import PackageTable from './PackageTable';
import logger from '@/utils/logger';
import {
  Plus,
  Pencil,
  Trash2,
  Search,
  Eye,
  TrendingUp,
  Package,
  Upload,
  Link2,
  ExternalLink,
  Copy,
  Check,
  X,
  Calendar,
  GripVertical,
  RefreshCw,
  Sparkles,
  Wand2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPackages = () => {
  const { getAuthHeaders } = useAdmin();
  const [packages, setPackages] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [tripTypes, setTripTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterType, setFilterType] = useState('all');
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdPackage, setCreatedPackage] = useState(null);
  const [copiedLink, setCopiedLink] = useState(false);
  
  // Image upload
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  
  // New trip type
  const [newTripType, setNewTripType] = useState('');
  const [showAddType, setShowAddType] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [editTypeName, setEditTypeName] = useState('');
  
  // Countries and cities/states for destination selection
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [states, setStates] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [citySearchOpen, setCitySearchOpen] = useState(false);
  
  // AI Itinerary Builder
  const [generatingItinerary, setGeneratingItinerary] = useState(false);
  const [itinerarySkeleton, setItinerarySkeleton] = useState('');
  const [generatedPackageTitle, setGeneratedPackageTitle] = useState('');
  const [generatedActivities, setGeneratedActivities] = useState([]);
  
  // Default Terms & Conditions (without bullets - frontend adds them)
  const defaultTermsConditions = `Booking Confirmation – A trip booking is considered confirmed only after the required deposit or full payment is received and acknowledged by the company.
Payment Terms – The remaining balance must be paid before the travel date as per the payment schedule communicated at the time of booking. Failure to complete payment within the given timeline may result in cancellation of the booking.
Pricing & Rate Changes – Package prices are subject to availability and may change due to fluctuations in airfare, hotel pricing, currency exchange rates, fuel surcharges, government taxes, or supplier costs until the booking is confirmed.
Third-Party Services – Hotels, airlines, cruise operators, transport providers, and activity organizers are independent service providers. The company acts only as a booking facilitator and is not responsible for their operational decisions, service quality, delays, or changes.
Itinerary Changes – The itinerary may be adjusted due to weather conditions, operational requirements, local regulations, or unforeseen circumstances. In such cases, reasonable alternatives will be arranged where possible.
Travel Documents – Travelers are responsible for ensuring that they carry valid passports, visas, permits, and identification documents required for the trip. Any issues arising due to incomplete or invalid documentation will be the traveler's responsibility.
Visa Assistance – Visa support or documentation guidance may be provided where applicable; however, visa approval is solely at the discretion of the respective embassy or consulate. The company cannot guarantee visa approval.
Flights & Transportation – Flight schedules, baggage policies, and transportation services are governed by the respective airline or transport provider. Any schedule changes, cancellations, or delays are beyond the company's control.
Hotel Policies – Hotel room allocation, check-in and check-out timings, and property facilities are subject to the policies of the respective hotel. Early check-in or late check-out is subject to availability.
Cancellations & Refunds – Cancellation charges may apply as per the cancellation policy communicated at the time of booking. Refund timelines depend on airlines, hotels, and other service providers involved in the booking.
Travel Insurance – Travelers are strongly advised to obtain comprehensive travel insurance covering medical emergencies, trip cancellations, delays, accidents, and baggage loss.
Traveler Conduct – Travelers are expected to behave responsibly and follow local laws and regulations. The company reserves the right to remove any traveler from a trip without refund if their behavior disrupts the safety or experience of others.
Personal Responsibility – Travelers are responsible for their personal belongings, health conditions, and participation in activities during the trip. The company will not be responsible for any personal loss, injury, or damage during travel.
Force Majeure – The company shall not be liable for disruptions caused by events beyond its control, including but not limited to natural disasters, political unrest, strikes, pandemics, government restrictions, or airline disruptions.
Limitation of Liability – The company's liability is limited to the amount paid for the travel services booked through the company and does not extend to indirect or consequential losses.
Jurisdiction – Any disputes arising from bookings or travel services shall be subject to the jurisdiction of the courts in the city where the company is registered.
Acceptance of Terms – By confirming a booking with the company, travelers acknowledge that they have read, understood, and accepted these terms and conditions.`;
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    country: '',
    city: '',
    destination: '',
    duration: '',
    price: '',
    category: 'domestic',
    types: [],
    image: '',
    highlights: '',
    inclusions: '',
    exclusions: '',
    terms_conditions: defaultTermsConditions,
    itinerary: [],
    is_active: true
  });

  const fetchData = useCallback(async () => {
    try {
      const [packagesRes, destinationsRes, typesRes, countriesRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/packages`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/admin/destinations`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/admin/trip-types`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/admin/destinations/countries`, { headers: getAuthHeaders() })
      ]);
      setPackages(packagesRes.data);
      setDestinations(destinationsRes.data);
      setTripTypes(typesRes.data.types || []);
      setCountries(countriesRes.data || []);
    } catch (error) {
      logger.error('Error fetching data:', error);
      toast.error('Failed to fetch packages');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchCitiesForCountry = async (countryName) => {
    if (!countryName) {
      setCities([]);
      setStates([]);
      return null;
    }
    
    setLoadingCities(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/admin/destinations/cities/${encodeURIComponent(countryName)}`,
        { headers: getAuthHeaders() }
      );
      setCities(response.data.cities || []);
      setStates(response.data.states || []);
      return response.data.category || 'international';
    } catch (error) {
      logger.error('Error fetching cities:', error);
      setCities([]);
      setStates([]);
      return 'international';
    } finally {
      setLoadingCities(false);
    }
  };

  const handleCountryChange = async (countryName) => {
    const category = await fetchCitiesForCountry(countryName);
    const destinationName = countryName; // Default to country name
    
    setFormData(prev => ({
      ...prev,
      country: countryName,
      city: '',
      destination: destinationName,
      category: category || 'international'
    }));
    
    // Auto-fetch suggested image
    fetchSuggestedImage(countryName, null);
  };

  const handleCityChange = (cityName) => {
    // Handle __none__ value as empty (country only)
    const actualCity = cityName === '__none__' ? '' : cityName;
    // Destination is city if selected, otherwise country
    const destinationName = actualCity || formData.country;
    
    setFormData(prev => ({
      ...prev,
      city: actualCity,
      destination: destinationName
    }));
    
    // Fetch new suggested image when city changes
    if (actualCity && formData.country) {
      fetchSuggestedImage(formData.country, actualCity);
    }
  };

  const fetchSuggestedImage = async (country, city) => {
    try {
      const params = new URLSearchParams({ country });
      if (city) params.append('city', city);
      
      const response = await axios.get(
        `${API_URL}/api/admin/destinations/suggest-image?${params}`,
        { headers: getAuthHeaders() }
      );
      
      if (response.data.image_url) {
        setFormData(prev => ({
          ...prev,
          image: response.data.image_url
        }));
        toast.success(`AI selected image for ${city || country}`);
      }
    } catch (error) {
      logger.error('Error fetching suggested image:', error);
    }
  };

  // Format duration from number to "XX Nights / XX Days"
  const formatDuration = (nights) => {
    const n = parseInt(nights);
    if (isNaN(n) || n < 1) return '';
    
    const nightStr = n.toString().padStart(2, '0');
    const days = n + 1;
    const dayStr = days.toString().padStart(2, '0');
    
    const nightWord = n === 1 ? 'Night' : 'Nights';
    const dayWord = days === 1 ? 'Day' : 'Days';
    
    return `${nightStr} ${nightWord} / ${dayStr} ${dayWord}`;
  };

  const handleDurationChange = (value) => {
    // Only allow numbers
    const numericValue = value.replace(/[^0-9]/g, '');
    if (numericValue.length <= 2) {
      setFormData(prev => ({ ...prev, duration: numericValue }));
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      country: '',
      city: '',
      destination: '',
      duration: '',
      price: '',
      category: 'domestic',
      types: [],
      image: '',
      highlights: '',
      inclusions: '',
      exclusions: '',
      terms_conditions: defaultTermsConditions,
      itinerary: [],
      is_active: true
    });
    setCities([]);
    setEditingPackage(null);
    // Reset AI itinerary states
    setItinerarySkeleton('');
    setGeneratedPackageTitle('');
    setGeneratedActivities([]);
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = async (pkg) => {
    setEditingPackage(pkg);
    
    // Get types - support both array and single type
    const types = pkg.types && pkg.types.length > 0 
      ? pkg.types 
      : (pkg.type ? [pkg.type] : []);
    
    // Convert itinerary activities array to string for editing
    const itineraryWithInputs = (pkg.itinerary || []).map(day => ({
      ...day,
      activitiesInput: (day.activities || []).join(', ')
    }));
    
    // Use stored country/city first, then try to determine from destination
    let country = pkg.country || '';
    let city = pkg.city || '';
    
    // If no country stored, try to find from destinations
    if (!country && pkg.destination) {
      const existingDest = destinations.find(d => d.name === pkg.destination);
      if (existingDest) {
        country = existingDest.country || '';
        city = existingDest.name !== existingDest.country ? existingDest.name : '';
      } else {
        // If destination matches a country name, use it as country
        const matchedCountry = countries.find(c => c.name === pkg.destination);
        if (matchedCountry) {
          country = matchedCountry.name;
        }
      }
    }
    
    // Fetch cities/states for the country
    if (country) {
      await fetchCitiesForCountry(country);
    }
    
    setFormData({
      title: pkg.title || '',
      country: country,
      city: city,
      destination: pkg.destination || '',
      duration: pkg.duration || '',
      price: pkg.price?.toString() || '',
      category: pkg.category === 'honeymoon' ? 'international' : (pkg.category || 'international'),
      types: types,
      image: pkg.image || '',
      highlights: pkg.highlights?.join(', ') || '',
      inclusions: pkg.inclusions?.join(', ') || '',
      exclusions: pkg.exclusions?.join(', ') || '',
      terms_conditions: pkg.terms_conditions?.join('\n') || '',
      itinerary: itineraryWithInputs,
      is_active: pkg.is_active !== false
    });
    setShowModal(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Client-side validation
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    const maxSize = 10 * 1024 * 1024; // 10MB

    if (!allowedTypes.includes(file.type)) {
      const ext = file.name.split('.').pop() || 'unknown';
      toast.error(`Unsupported format (.${ext}). Use JPG, PNG, WebP, or GIF`);
      e.target.value = '';
      return;
    }
    if (file.size > maxSize) {
      const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
      toast.error(`File too large (${sizeMB} MB). Maximum is 10 MB`);
      e.target.value = '';
      return;
    }
    if (file.size === 0) {
      toast.error('File is empty (0 bytes)');
      e.target.value = '';
      return;
    }
    
    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/upload-image`,
        formDataUpload,
        { 
          headers: { 
            ...getAuthHeaders(),
            'Content-Type': 'multipart/form-data'
          } 
        }
      );
      const imageUrl = `${API_URL}${response.data.url}`;
      setFormData({ ...formData, image: imageUrl });
      toast.success('Image uploaded successfully');
    } catch (error) {
      const detail = error.response?.data?.detail || 'Upload failed. Please try again.';
      toast.error(detail);
    } finally {
      setUploading(false);
    }
  };

  const toggleType = (type) => {
    const currentTypes = formData.types || [];
    if (currentTypes.includes(type)) {
      setFormData({ ...formData, types: currentTypes.filter(t => t !== type) });
    } else {
      setFormData({ ...formData, types: [...currentTypes, type] });
    }
  };

  const handleAddTripType = async () => {
    if (!newTripType.trim()) return;
    
    try {
      await axios.post(
        `${API_URL}/api/admin/trip-types?type_name=${encodeURIComponent(newTripType)}`,
        {},
        { headers: getAuthHeaders() }
      );
      setTripTypes([...tripTypes, newTripType.toLowerCase()]);
      setNewTripType('');
      setShowAddType(false);
      toast.success('Trip type added');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add trip type');
    }
  };

  const handleEditTripType = async (oldName) => {
    if (!editTypeName.trim() || editTypeName.trim().toLowerCase() === oldName.toLowerCase()) {
      setEditingType(null);
      return;
    }
    try {
      const resp = await axios.put(
        `${API_URL}/api/admin/trip-types/${encodeURIComponent(oldName)}?new_name=${encodeURIComponent(editTypeName.trim())}`,
        {},
        { headers: getAuthHeaders() }
      );
      setTripTypes(resp.data.types);
      setEditingType(null);
      toast.success('Trip type updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update trip type');
    }
  };

  const handleDeleteTripType = async (typeName) => {
    try {
      const resp = await axios.delete(
        `${API_URL}/api/admin/trip-types/${encodeURIComponent(typeName)}`,
        { headers: getAuthHeaders() }
      );
      setTripTypes(resp.data.types);
      toast.success('Trip type deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete trip type');
    }
  };

  // Itinerary functions
  const addItineraryDay = () => {
    const newDay = {
      day: formData.itinerary.length + 1,
      title: '',
      description: '',
      activitiesInput: '' // Store as string during editing
    };
    setFormData({ ...formData, itinerary: [...formData.itinerary, newDay] });
  };

  const updateItineraryDay = (index, field, value) => {
    const updated = [...formData.itinerary];
    updated[index][field] = value;
    setFormData({ ...formData, itinerary: updated });
  };

  const removeItineraryDay = (index) => {
    const updated = formData.itinerary.filter((_, i) => i !== index);
    // Re-number days
    updated.forEach((day, i) => { day.day = i + 1; });
    setFormData({ ...formData, itinerary: updated });
  };

  // AI Itinerary Generation
  const generateAIItinerary = async () => {
    if (!formData.country) {
      toast.error('Please select a country first');
      return;
    }
    
    if (!formData.duration) {
      toast.error('Please enter the number of nights first');
      return;
    }
    
    const nights = parseInt(formData.duration);
    if (isNaN(nights) || nights < 1) {
      toast.error('Please enter a valid number of nights');
      return;
    }
    
    setGeneratingItinerary(true);
    
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/packages/generate-itinerary`,
        {
          country: formData.country,
          city: formData.city || null,
          nights: nights,
          skeleton: itinerarySkeleton || null
        },
        { headers: getAuthHeaders() }
      );
      
      if (response.data.success) {
        // Set generated package title suggestion
        if (response.data.package_title) {
          let title = response.data.package_title;
          // Remove any surrounding quotes if present
          if (typeof title === 'string' && title.startsWith('"') && title.endsWith('"')) {
            title = title.slice(1, -1);
          }
          setGeneratedPackageTitle(title);
          // Auto-fill title if empty
          if (!formData.title) {
            setFormData(prev => ({ ...prev, title: title }));
          }
        }
        
        // Set generated activities
        if (response.data.activities && response.data.activities.length > 0) {
          setGeneratedActivities(response.data.activities);
          // Auto-fill highlights if empty
          if (!formData.highlights) {
            setFormData(prev => ({ ...prev, highlights: response.data.activities.join(', ') }));
          }
        }
        
        // Convert itinerary to form format
        const formattedItinerary = response.data.itinerary.map(day => ({
          day: day.day,
          title: typeof day.title === 'string' ? day.title : String(day.title || ''),
          description: typeof day.description === 'string' ? day.description : String(day.description || ''),
          activitiesInput: Array.isArray(day.activities) ? day.activities.join(', ') : (day.activities || '')
        }));
        
        setFormData(prev => ({ ...prev, itinerary: formattedItinerary }));
        toast.success('AI Itinerary generated successfully!');
      }
    } catch (error) {
      logger.error('AI Itinerary generation error:', error);
      const errorMessage = error.response?.data?.detail;
      if (typeof errorMessage === 'string') {
        toast.error(errorMessage);
      } else if (typeof errorMessage === 'object') {
        toast.error('Failed to generate itinerary. Please check input parameters.');
      } else {
        toast.error('Failed to generate itinerary');
      }
    } finally {
      setGeneratingItinerary(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.country) {
      toast.error('Please select a country');
      return;
    }
    
    if (formData.types.length === 0) {
      toast.error('Please select at least one trip type');
      return;
    }
    
    if (!formData.duration) {
      toast.error('Please enter duration (number of nights)');
      return;
    }
    
    // Destination is city if selected, otherwise country
    const destination = formData.city || formData.country;
    
    // Format duration from number to "XX Nights / XX Days"
    const formattedDuration = formatDuration(formData.duration);
    
    // Convert itinerary activities from string to array
    const processedItinerary = formData.itinerary.map(day => ({
      day: day.day,
      title: day.title,
      description: day.description,
      activities: (day.activitiesInput || '').split(',').map(s => s.trim()).filter(Boolean)
    }));
    
    const payload = {
      title: formData.title,
      destination: destination,
      country: formData.country,
      city: formData.city || '',
      duration: formattedDuration,
      price: parseInt(formData.price),
      category: formData.category,
      type: formData.types[0] || 'group',  // First type for backward compatibility
      types: formData.types,
      image: formData.image,
      highlights: formData.highlights.split(',').map(s => s.trim()).filter(Boolean),
      inclusions: formData.inclusions.split(',').map(s => s.trim()).filter(Boolean),
      exclusions: formData.exclusions.split(',').map(s => s.trim()).filter(Boolean),
      terms_conditions: formData.terms_conditions.split('\n').map(s => s.trim()).filter(Boolean),
      itinerary: processedItinerary,
      is_active: formData.is_active
    };

    try {
      if (editingPackage) {
        await axios.put(
          `${API_URL}/api/admin/packages/${editingPackage.id}`,
          payload,
          { headers: getAuthHeaders() }
        );
        toast.success('Package updated successfully');
        setShowModal(false);
      } else {
        const response = await axios.post(
          `${API_URL}/api/admin/packages`,
          payload,
          { headers: getAuthHeaders() }
        );
        setCreatedPackage(response.data);
        setShowModal(false);
        setShowSuccessModal(true);
      }
      resetForm();
      fetchData();
    } catch (error) {
      logger.error('Error saving package:', error);
      toast.error(error.response?.data?.detail || 'Failed to save package');
    }
  };

  const handleDelete = async (packageId) => {
    try {
      await axios.delete(`${API_URL}/api/admin/packages/${packageId}`, {
        headers: getAuthHeaders()
      });
      toast.success('Package deleted successfully');
      setDeleteConfirm(null);
      fetchData();
    } catch (error) {
      logger.error('Error deleting package:', error);
      toast.error('Failed to delete package');
    }
  };

  const togglePackageActive = async (pkg) => {
    try {
      await axios.put(
        `${API_URL}/api/admin/packages/${pkg.id}`,
        { is_active: !pkg.is_active },
        { headers: getAuthHeaders() }
      );
      toast.success(`Package ${!pkg.is_active ? 'activated' : 'deactivated'}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update package status');
    }
  };

  const getPackageUrl = (pkg) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/packages/${pkg.package_code}`;
  };

  const copyPackageLink = (pkg) => {
    navigator.clipboard.writeText(getPackageUrl(pkg));
    setCopiedLink(true);
    toast.success('Link copied to clipboard');
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const openPackageInNewTab = (pkg) => {
    window.open(getPackageUrl(pkg), '_blank');
  };

  const filteredPackages = packages.filter(pkg => {
    const matchesSearch = pkg.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         pkg.destination.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         pkg.package_code?.includes(searchTerm);
    const matchesCategory = filterCategory === 'all' || pkg.category === filterCategory;
    const pkgTypes = pkg.types || (pkg.type ? [pkg.type] : []);
    const matchesType = filterType === 'all' || pkgTypes.includes(filterType);
    return matchesSearch && matchesCategory && matchesType;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-packages">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Package Management</h1>
          <p className="text-slate-500">Manage tour packages, pricing, and details</p>
        </div>
        <Button
          onClick={openCreateModal}
          className="bg-red-600 hover:bg-red-700"
          data-testid="add-package-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Add Package
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by name, destination, or code..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-packages"
              />
            </div>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="w-full md:w-48" data-testid="filter-category">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="international">International</SelectItem>
                <SelectItem value="domestic">Domestic</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-full md:w-48" data-testid="filter-type">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {tripTypes.map(type => (
                  <SelectItem key={type} value={type} className="capitalize">{type}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Packages Table */}
      <PackageTable
        filteredPackages={filteredPackages}
        openEditModal={openEditModal}
        setDeleteConfirm={setDeleteConfirm}
        togglePackageActive={togglePackageActive}
        copyPackageLink={copyPackageLink}
        openPackageInNewTab={openPackageInNewTab}
      />

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingPackage ? 'Edit Package' : 'Create New Package'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="title">Package Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  data-testid="input-title"
                />
              </div>
              
              {/* Country Selection - Mandatory */}
              <div className="space-y-2">
                <Label htmlFor="country">
                  Country * 
                  <span className="text-xs text-slate-400 font-normal ml-1">(Mandatory)</span>
                </Label>
                <Select
                  value={formData.country}
                  onValueChange={handleCountryChange}
                >
                  <SelectTrigger data-testid="input-country">
                    <SelectValue placeholder="Select country..." />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    <SelectGroup>
                      <SelectLabel className="text-green-600 bg-green-50 -mx-1 px-3">
                        Domestic
                      </SelectLabel>
                      {countries.filter(c => c.category === 'domestic').map(country => (
                        <SelectItem key={country.code} value={country.name}>
                          🇮🇳 {country.name}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                    <SelectGroup>
                      <SelectLabel className="text-blue-600 bg-blue-50 -mx-1 px-3">
                        International
                      </SelectLabel>
                      {countries.filter(c => c.category === 'international').map(country => (
                        <SelectItem key={country.code} value={country.name}>
                          {country.name}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
                {formData.country && (
                  <p className={`text-xs px-2 py-1 rounded ${
                    formData.category === 'domestic' 
                      ? 'bg-green-50 text-green-700' 
                      : 'bg-blue-50 text-blue-700'
                  }`}>
                    Auto-tagged as <strong>{formData.category}</strong>
                  </p>
                )}
              </div>
              
              {/* City/State Selection - Searchable */}
              <div className="space-y-2">
                <Label htmlFor="city">
                  City/State/Province
                  <span className="text-xs text-slate-400 font-normal ml-1">(Optional - Type to search)</span>
                </Label>
                {formData.country ? (
                  loadingCities ? (
                    <div className="flex items-center gap-2 text-sm text-slate-500 py-2">
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Loading locations...
                    </div>
                  ) : (cities.length > 0 || states.length > 0) ? (
                    <Popover open={citySearchOpen} onOpenChange={setCitySearchOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={citySearchOpen}
                          className="w-full justify-between font-normal"
                          data-testid="input-city"
                        >
                          {formData.city ? (
                            <span className="flex items-center gap-2">
                              {states.includes(formData.city) ? '📍' : '🏙️'} {formData.city}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">Search city/state/province...</span>
                          )}
                          <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[400px] p-0" align="start">
                        <Command>
                          <CommandInput placeholder="Type to search..." />
                          <CommandList className="max-h-[300px]">
                            <CommandEmpty>No location found.</CommandEmpty>
                            {formData.city && (
                              <CommandItem
                                onSelect={() => {
                                  handleCityChange('');
                                  setCitySearchOpen(false);
                                }}
                                className="text-slate-500"
                              >
                                ✕ Clear selection
                              </CommandItem>
                            )}
                            {states.length > 0 && (
                              <CommandGroup heading="States / Provinces">
                                {states.map(state => (
                                  <CommandItem
                                    key={`state-${state}`}
                                    value={state}
                                    onSelect={() => {
                                      handleCityChange(state);
                                      setCitySearchOpen(false);
                                    }}
                                  >
                                    <span className={formData.city === state ? "font-semibold" : ""}>
                                      📍 {state}
                                    </span>
                                    {formData.city === state && <Check className="ml-auto h-4 w-4" />}
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            )}
                            {cities.length > 0 && (
                              <CommandGroup heading="Cities">
                                {cities.map(city => (
                                  <CommandItem
                                    key={`city-${city}`}
                                    value={city}
                                    onSelect={() => {
                                      handleCityChange(city);
                                      setCitySearchOpen(false);
                                    }}
                                  >
                                    <span className={formData.city === city ? "font-semibold" : ""}>
                                      🏙️ {city}
                                    </span>
                                    {formData.city === city && <Check className="ml-auto h-4 w-4" />}
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            )}
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  ) : (
                    <Input
                      id="city"
                      value={formData.city}
                      onChange={(e) => handleCityChange(e.target.value)}
                      placeholder="Enter city/state name..."
                      data-testid="input-city"
                    />
                  )
                ) : (
                  <p className="text-sm text-slate-400 py-2">Select a country first</p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="duration">Duration * <span className="text-xs text-slate-400 font-normal">(Enter number of nights)</span></Label>
                <div className="flex gap-2 items-center">
                  <Input
                    id="duration"
                    type="text"
                    placeholder="e.g., 4"
                    value={formData.duration}
                    onChange={(e) => handleDurationChange(e.target.value)}
                    className="w-20"
                    maxLength={2}
                    required
                    data-testid="input-duration"
                  />
                  <span className="text-sm text-slate-500">nights</span>
                </div>
                {formData.duration && (
                  <div className="text-sm font-medium text-green-600 bg-green-50 px-3 py-2 rounded-lg">
                    Preview: {formatDuration(formData.duration)}
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="price">Price (₹) *</Label>
                <Input
                  id="price"
                  type="number"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  required
                  data-testid="input-price"
                />
              </div>
            </div>

            {/* Trip Types - Multiple Selection */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Trip Types * (Select multiple)</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowAddType(!showAddType)}
                  className="text-red-600 hover:text-red-700"
                >
                  <Plus size={14} className="mr-1" /> Add Type
                </Button>
              </div>
              {showAddType && (
                <div className="flex gap-2 mb-2">
                  <Input
                    placeholder="Enter new type..."
                    value={newTripType}
                    onChange={(e) => setNewTripType(e.target.value)}
                    className="flex-1"
                  />
                  <Button type="button" size="sm" onClick={handleAddTripType}>Add</Button>
                  <Button type="button" size="sm" variant="ghost" onClick={() => setShowAddType(false)}>
                    <X size={16} />
                  </Button>
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                {tripTypes.map(type => (
                  <div key={type} className="relative group/type flex items-center gap-0.5">
                    {editingType === type ? (
                      <div className="flex items-center gap-1">
                        <Input
                          value={editTypeName}
                          onChange={(e) => setEditTypeName(e.target.value)}
                          className="h-8 w-28 text-sm"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') { e.preventDefault(); handleEditTripType(type); }
                            if (e.key === 'Escape') setEditingType(null);
                          }}
                        />
                        <button type="button" onClick={() => handleEditTripType(type)} className="p-1 text-green-600 hover:bg-green-50 rounded">
                          <Check size={14} />
                        </button>
                        <button type="button" onClick={() => setEditingType(null)} className="p-1 text-slate-400 hover:bg-slate-100 rounded">
                          <X size={14} />
                        </button>
                      </div>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => toggleType(type)}
                          className={`px-3 py-1.5 text-sm rounded-full border transition-colors capitalize ${
                            formData.types?.includes(type)
                              ? 'bg-red-600 text-white border-red-600'
                              : 'bg-white text-slate-700 border-slate-300 hover:border-red-300'
                          }`}
                          data-testid={`trip-type-${type}`}
                        >
                          {formData.types?.includes(type) && <Check size={14} className="inline mr-1" />}
                          {type}
                        </button>
                        <div className="hidden group-hover/type:flex items-center gap-0.5 ml-0.5">
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setEditingType(type); setEditTypeName(type); }}
                            className="p-1 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                            title="Edit"
                            data-testid={`edit-trip-type-${type}`}
                          >
                            <Pencil size={12} />
                          </button>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); handleDeleteTripType(type); }}
                            className="p-1 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded"
                            title="Delete"
                            data-testid={`delete-trip-type-${type}`}
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
              {formData.types?.length === 0 && (
                <p className="text-xs text-red-500">Please select at least one trip type</p>
              )}
            </div>

            {/* Image Upload */}
            <div className="space-y-2">
              <Label>Package Image *</Label>
              <div className="flex gap-2">
                <Input
                  type="url"
                  placeholder="Image URL or upload from system"
                  value={formData.image}
                  onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                  className="flex-1"
                  data-testid="input-image"
                />
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImageUpload}
                  accept="image/*"
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                >
                  {uploading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600" />
                  ) : (
                    <><Upload size={16} className="mr-2" /> Upload</>
                  )}
                </Button>
              </div>
              {formData.image && (
                <div className="mt-2">
                  <img src={formData.image} alt="Preview" className="h-24 w-auto rounded-lg object-cover" />
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="highlights">Highlights (comma-separated)</Label>
              <Textarea
                id="highlights"
                placeholder="Burj Khalifa, Desert Safari, Dubai Mall..."
                value={formData.highlights}
                onChange={(e) => setFormData({ ...formData, highlights: e.target.value })}
                data-testid="input-highlights"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="inclusions">Inclusions (comma-separated)</Label>
              <Textarea
                id="inclusions"
                placeholder="Flights, Hotels, Transfers, Breakfast..."
                value={formData.inclusions}
                onChange={(e) => setFormData({ ...formData, inclusions: e.target.value })}
                data-testid="input-inclusions"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="exclusions">Exclusions (comma-separated)</Label>
              <Textarea
                id="exclusions"
                placeholder="Lunch, Dinner, Personal Expenses..."
                value={formData.exclusions}
                onChange={(e) => setFormData({ ...formData, exclusions: e.target.value })}
                data-testid="input-exclusions"
              />
            </div>

            {/* Itinerary Builder */}
            <div className="space-y-3 border-t pt-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <Label className="text-base font-semibold flex items-center gap-2">
                  <Calendar size={18} /> Day-wise Itinerary
                </Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addItineraryDay}
                    className="text-red-600 border-red-200 hover:bg-red-50"
                  >
                    <Plus size={14} className="mr-1" /> Add Day
                  </Button>
                </div>
              </div>
              
              {/* AI Itinerary Generator */}
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-lg border border-purple-200">
                <div className="flex items-center gap-2 mb-3">
                  <Wand2 className="w-5 h-5 text-purple-600" />
                  <span className="font-semibold text-purple-800">AI Itinerary Builder</span>
                  <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">Powered by AI</span>
                </div>
                <p className="text-sm text-purple-700 mb-3">
                  Automatically generate a professional day-wise itinerary based on your destination and duration.
                </p>
                
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs text-purple-600 mb-1 block">
                      Skeleton / Outline (Optional)
                    </Label>
                    <Textarea
                      placeholder="Paste your itinerary skeleton here... E.g.:
Day 1 - Arrival in Dubai
Day 2 - Dubai City Tour + View at the Palm
Day 3 - Desert Safari
Day 4 - Abu Dhabi Day Trip
Day 5 - Dubai Mall + Departure

Or leave empty for AI to create from scratch."
                      value={itinerarySkeleton}
                      onChange={(e) => setItinerarySkeleton(e.target.value)}
                      className="min-h-[100px] bg-white border-purple-200 focus:border-purple-400"
                      data-testid="itinerary-skeleton-input"
                    />
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <Button
                      type="button"
                      onClick={generateAIItinerary}
                      disabled={generatingItinerary || !formData.country || !formData.duration}
                      className="bg-purple-600 hover:bg-purple-700 text-white"
                      data-testid="generate-itinerary-btn"
                    >
                      {generatingItinerary ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-2" />
                          Generate with AI
                        </>
                      )}
                    </Button>
                    {(!formData.country || !formData.duration) && (
                      <span className="text-xs text-purple-600">
                        Select country and enter nights first
                      </span>
                    )}
                  </div>
                </div>
                
                {/* Show generated package title suggestion */}
                {generatedPackageTitle && (
                  <div className="mt-3 p-2 bg-white rounded border border-purple-200">
                    <p className="text-xs text-purple-600 mb-1">AI Suggested Title:</p>
                    <p className="text-sm font-medium text-purple-800">{generatedPackageTitle}</p>
                  </div>
                )}
                
                {/* Show generated activities */}
                {generatedActivities.length > 0 && (
                  <div className="mt-3 p-2 bg-white rounded border border-purple-200">
                    <p className="text-xs text-purple-600 mb-1">AI Suggested Activities & Experiences:</p>
                    <div className="flex flex-wrap gap-1">
                      {generatedActivities.slice(0, 8).map((activity, idx) => (
                        <span key={`activity-${activity}-${idx}`} className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full">
                          {activity}
                        </span>
                      ))}
                      {generatedActivities.length > 8 && (
                        <span className="text-xs text-purple-500">+{generatedActivities.length - 8} more</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              {formData.itinerary.length === 0 ? (
                <div className="text-center py-6 bg-slate-50 rounded-lg border-2 border-dashed border-slate-200">
                  <Calendar className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                  <p className="text-sm text-slate-500">No itinerary added yet</p>
                  <p className="text-xs text-slate-400 mt-1">Use the AI Builder above or add days manually</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-600">
                      {formData.itinerary.length} {formData.itinerary.length === 1 ? 'Day' : 'Days'} Added
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setFormData({ ...formData, itinerary: [] })}
                      className="text-red-500 text-xs"
                    >
                      Clear All
                    </Button>
                  </div>
                  {formData.itinerary.map((day, index) => (
                    <div key={`day-${day.day}-${index}`} className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <GripVertical size={16} className="text-slate-400" />
                          <span className="font-semibold text-red-600">Day {day.day}</span>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeItineraryDay(index)}
                          className="h-7 w-7 text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs text-slate-500">Day Title</Label>
                          <Input
                            placeholder="e.g., Arrival in Dubai"
                            value={day.title}
                            onChange={(e) => updateItineraryDay(index, 'title', e.target.value)}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500">Activities (comma-separated)</Label>
                          <Input
                            placeholder="Airport pickup, Hotel check-in, Evening free"
                            value={day.activitiesInput || ''}
                            onChange={(e) => updateItineraryDay(index, 'activitiesInput', e.target.value)}
                            className="mt-1"
                          />
                        </div>
                      </div>
                      <div className="mt-3">
                        <Label className="text-xs text-slate-500 mb-1 block">Description (with formatting)</Label>
                        <RichTextEditor
                          value={day.description || ''}
                          onChange={(value) => updateItineraryDay(index, 'description', value)}
                          placeholder="Detailed description of the day... Use bold, italic, or bullet points"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="terms">Terms & Conditions (one per line)</Label>
              <Textarea
                id="terms"
                placeholder="50% advance payment required&#10;Cancellation charges apply"
                value={formData.terms_conditions}
                onChange={(e) => setFormData({ ...formData, terms_conditions: e.target.value })}
                rows={3}
                data-testid="input-terms"
              />
            </div>

            <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="w-4 h-4 rounded border-gray-300"
                data-testid="input-active"
              />
              <div>
                <Label htmlFor="is_active" className="font-medium">Show on Website</Label>
                <p className="text-xs text-slate-500">
                  {formData.is_active 
                    ? 'Package will be visible in website listings' 
                    : 'Package will be hidden from listings but accessible via direct link'}
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowModal(false)}
              >
                Cancel
              </Button>
              <Button type="submit" className="bg-red-600 hover:bg-red-700" data-testid="save-package-btn">
                {editingPackage ? 'Update Package' : 'Create Package'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Success Modal with Link Options */}
      <Dialog open={showSuccessModal} onOpenChange={setShowSuccessModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <Check className="w-6 h-6" /> Package Created Successfully!
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-slate-600">
              Your package <strong>{createdPackage?.title}</strong> has been created with code{' '}
              <span className="font-mono bg-slate-100 px-2 py-1 rounded">#{createdPackage?.package_code}</span>
            </p>
            <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
              <Link2 className="w-5 h-5 text-slate-400" />
              <Input
                value={createdPackage ? getPackageUrl(createdPackage) : ''}
                readOnly
                className="flex-1 bg-white"
              />
            </div>
            <div className="flex gap-2">
              <Button
                className="flex-1"
                variant="outline"
                onClick={() => {
                  if (createdPackage) copyPackageLink(createdPackage);
                }}
              >
                <Copy className="w-4 h-4 mr-2" /> Copy Link
              </Button>
              <Button
                className="flex-1 bg-red-600 hover:bg-red-700"
                onClick={() => {
                  if (createdPackage) openPackageInNewTab(createdPackage);
                }}
              >
                <ExternalLink className="w-4 h-4 mr-2" /> Open in New Tab
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowSuccessModal(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Package</DialogTitle>
          </DialogHeader>
          <p className="text-slate-600">
            Are you sure you want to delete <strong>{deleteConfirm?.title}</strong>?
            This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
              Cancel
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700"
              onClick={() => handleDelete(deleteConfirm?.id)}
              data-testid="confirm-delete-btn"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPackages;
